/*
  Журнал загрузок - «экран здоровья импорта» (§7.4.3).

  Вынесен из `ImportPage` 10.09.2026, когда к выгрузке начислений добавилась
  загрузка выписки: страница получила вкладки, а журнал стал общим для обоих
  видов файла и показывает, какой именно загружали.

  🔴 Причина отказа лежит В ОКНЕ, а не в ячейке (10.09.2026, замечание
  владельца). Объяснение отказа - предложение в полторы сотни знаков; в колонке
  оно давало строку высотой 345 пикселей против 48 у обычной (замер того же
  дня), и журнал переставал читаться: чтобы дойти до предыдущей загрузки,
  приходилось прокручивать чужую ошибку целиком. В строке остаётся метка
  состояния и слово «подробнее», текст открывается нажатием.

  Обрезать текст в ячейке нельзя: отказ говорит, ЧТО сделать дальше
  («подтвердите сопоставление колонок вручную»), и обрезанный на середине он
  теряет ровно эту часть.
*/

import { useState } from 'react'

import { dateTime, grouped } from '../api/format.js'
import { Empty, Loading, Tag } from './Common.jsx'
import DeferredSettlement from './DeferredSettlement.jsx'
import Modal from './Modal.jsx'
import PageBar from './PageBar.jsx'
import JournalFile from './upload/JournalFile.jsx'

const STATUS_TITLES = {
  completed: 'загружено',
  failed: 'не удалось',
  // С 19.10.2026 так загрузки не кончаются - подпись нужна старым строкам.
  rejected_anomalous_volume: 'остановлено: необычный объём',
  running: 'выполняется',
}

const KIND_TITLES = {
  charges: 'начисления',
  payments: 'поступления',
  registry: 'реестр должников',
  residents: 'домовая книга',
  identities: 'сведения о должниках',
}

/*
  Журнал не обрезается ничем и растёт с каждой выгрузкой: на демонстрационном
  портфеле в нём уже сорок пять записей, и страница вытянулась на три с половиной
  тысячи пикселей (замер 09.09.2026). Смотрят при этом почти всегда последнюю -
  убедиться, что файл прошёл.

  С 21.10.2026 строки режет СЕРВЕР: `journal` - уже страница, `bar` - свойства общей
  панели (`PageBar`) из `usePagedList`, которым пользуется `StepJournal`. Вместо
  прежнего «Показать ещё» (потом браузерного `usePaging`) здесь только рисунок.
*/

/*
  Состояние строки выводится из статуса И из чисел (аудит 26.10.2026, расхождение 11):
  `completed` означает только «загрузка дошла до конца», а не «строки приняты».
  «Загружено» зелёным при «принято 0, отклонено 2» читалось как успех, хотя в базу
  не попало ничего. Зелёной остаётся загрузка, где отклонённых нет.
*/
function rowState(run) {
  if (run.status !== 'completed') {
    return { kind: 'warn', title: STATUS_TITLES[run.status] ?? run.status }
  }
  if (run.rows_total > 0 && run.rows_accepted === 0) {
    return { kind: 'burn', title: 'ничего не принято' }
  }
  if (run.rows_rejected > 0) return { kind: 'warn', title: 'загружено частично' }
  return { kind: 'calm', title: STATUS_TITLES.completed }
}

// Подпись окна подробностей: чем была загрузка, когда и чем кончилась.
function noteFor(run) {
  /* Незнакомый вид файла называется как есть, а не подставляется начислениями
     (01.10.2026): видов стало четыре, и молчаливая подмена однажды назвала бы
     домовую книгу долгом. */
  const kind = KIND_TITLES[run.kind] ?? run.kind
  return `${kind} · ${dateTime(run.started_at)} · ${rowState(run).title}`
}

/*
  Отложенное погашение на строке журнала (19.10.2026): метка и «решить», а само
  решение - окном, по той же причине, что отказ: объяснение длинное. Решённое
  остаётся короткой пометкой - что сделано, видно и через месяц.
*/
const DECISIONS = { settled: 'погашено по решению', kept: 'оставлено как есть' }

function Deferral({ run, onOpen }) {
  if (run.settle_decision) {
    return <span className="muted">отложенное: {DECISIONS[run.settle_decision]}</span>
  }
  if (!(run.deferred_rows > 0)) return null
  return (
    <>
      <Tag kind="warn">погашение отложено</Tag>
      <button
        type="button"
        className="link-button"
        aria-label={`Решить об отложенном погашении: ${run.filename}`}
        onClick={onOpen}
      >
        решить
      </button>
    </>
  )
}

export default function ImportJournal({ journal, bar, onChanged }) {
  /*
    Загрузка, чьи подробности открыты окном, или `null`. Хранится вся строка
    журнала, а не один текст отказа: в окне нужны ещё имя файла, время и вид
    загрузки - без них непонятно, о какой из сорока пяти строк идёт речь.
  */
  const [details, setDetails] = useState(null)
  const [deciding, setDeciding] = useState(null)
  // Решение по отложенным строкам в пути: окно не закрывается (ревью 30.10.2026).
  const [decidingBusy, setDecidingBusy] = useState(false)

  if (!journal) return <Loading />

  if (journal.length === 0) {
    /* Пустота называется по месту, а не по всему разделу (01.10.2026): журнал
       отобран по виду файла, и прежнее «Первая выгрузка покажет, сколько
       должников в базе» в шаге домовой книги обещало бы не то. Слова подходят и
       шагу ленты, и вкладке - журнал один и тот же. */
    return (
      <Empty title="Файлов здесь ещё не было">
        Загрузите файл выше - и строка появится здесь: имя файла, время, сколько строк принято и
        сколько осталось вам.
      </Empty>
    )
  }

  return (
    <div className="card">
      <div className="table-scroll">
        <table className="table table--fixed table--imports">
          <thead>
            {/* Колонки «Что грузили» здесь нет с 01.10.2026: журнал отобран по
                виду файла, и в шаге она повторяла бы его заголовок в каждой
                строке. Вид загрузки остался в окне подробностей - там он нужен,
                потому что окно открывается поверх всего. */}
            <tr>
              <th>Файл</th>
              <th>Когда</th>
              <th>Состояние</th>
              <th className="table__num">Строк</th>
              <th className="table__num">Принято</th>
              <th className="table__num">Отклонено</th>
            </tr>
          </thead>
          <tbody>
            {journal.map((run) => (
              <tr key={run.import_run_id}>
                <td>
                  <JournalFile run={run} />
                </td>
                <td data-label="Когда">{dateTime(run.started_at)}</td>
                {/* Метка и «подробнее» в одной строке; на узком экране подпись
                    колонки встаёт над ними (`table__block`). */}
                <td className="table__block" data-label="Состояние">
                  <div className="row">
                    <Tag kind={rowState(run).kind}>{rowState(run).title}</Tag>
                    {run.error_message ? (
                      <button
                        type="button"
                        className="link-button"
                        /* Кнопок «подробнее» в журнале столько же, сколько
                           отказов: без имени файла читалка экрана назовёт их
                           все одинаково, и выбрать нужную будет нельзя. */
                        aria-label={`Подробнее об отказе: ${run.filename}`}
                        onClick={() => setDetails(run)}
                      >
                        подробнее
                      </button>
                    ) : null}
                    {/* Месяцы, погашенные как пропавшие из отчёта или среза 1С
                        (16.10.2026): долг уменьшился не оплатой, и это видно
                        здесь, а не только на карточках. Колонкой не делается:
                        у большинства загрузок здесь ноль. */}
                    {run.rows_settled > 0 ? (
                      <span className="muted">погашено месяцев: {run.rows_settled}</span>
                    ) : null}
                    {/* Старшинство источников (23.10.2026): принятые строки, которые файл
                        не записал, потому что их внесли руками или принёс обмен 1С.
                        Без пометки «принято 600» при записанных 200 читалось бы как
                        успех. */}
                    {run.rows_kept_by_hand > 0 ? (
                      <span className="muted">не записано, внесено руками: {run.rows_kept_by_hand}</span>
                    ) : null}
                    {run.rows_kept_by_onec > 0 ? (
                      <span className="muted">не записано, данные из 1С: {run.rows_kept_by_onec}</span>
                    ) : null}
                    {/* 26.10.2026, строка 193: обмен принёс месяц под другими услугами. */}
                    {run.rows_displaced > 0 ? (
                      <span className="muted">вытеснено строк файла данными 1С: {run.rows_displaced}</span>
                    ) : null}
                    {/* Загрузка по выбору (16.10.2026): «принято 2 строки из
                        600» без этой пометки читалось бы как сбой. У загрузки
                        всего файла пометки нет. */}
                    {run.chosen_accounts > 0 ? (
                      <span className="muted">выбрано счетов: {grouped(run.chosen_accounts)}</span>
                    ) : null}
                    <Deferral run={run} onOpen={() => setDeciding(run)} />
                  </div>
                </td>
                <td className="table__num" data-label="Строк">
                  {grouped(run.rows_total)}
                </td>
                <td className="table__num" data-label="Принято">
                  {grouped(run.rows_accepted)}
                </td>
                <td className="table__num" data-label="Отклонено">
                  {grouped(run.rows_rejected)}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <PageBar {...bar} what="загрузок" />
      {details ? (
        <Modal
          title={details.filename}
          note={noteFor(details)}
          onClose={() => setDetails(null)}
        >
          <div className="modal__text">{details.error_message}</div>
          <div className="row row--tight">
            {/* На этом шаге отменять нечего - окно только показывает причину
                (правило 10.3 скилла `frontend`). */}
            <button type="button" className="button" onClick={() => setDetails(null)}>
              Закрыть
            </button>
          </div>
        </Modal>
      ) : null}
      {deciding ? (
        <Modal
          title={deciding.filename}
          note={noteFor(deciding)}
          busy={decidingBusy}
          onClose={() => setDeciding(null)}
        >
          <DeferredSettlement
            runId={deciding.import_run_id}
            doubt={deciding.volume_doubt}
            count={deciding.deferred_rows}
            onDecided={onChanged}
            onBusy={setDecidingBusy}
          />
          <div className="row row--tight">
            <button
              type="button"
              className="button button--quiet"
              disabled={decidingBusy}
              onClick={() => setDeciding(null)}
            >
              Закрыть
            </button>
          </div>
        </Modal>
      ) : null}
    </div>
  )
}
